import React, { useState, useEffect } from "react";
import { validateEmail } from "../../../utils/formValidate";

interface ShareModalProps {
    show: boolean;
    onClose: () => void;
    onSubmit: (email: string, subject: string, message: string) => void;
    title: string;
}

const ShareModal: React.FC<ShareModalProps> = ({
    show,
    onClose,
    onSubmit,
    title
}) => {
    const [email, setEmail] = useState("");
    const [subject, setSubject] = useState("");
    const [message, setMessage] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [emailError, setEmailError] = useState("");

    // Prevent body scroll when modal is open
    useEffect(() => {
        if (show) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }

        return () => {
            document.body.style.overflow = '';
        };
    }, [show]);

    const handleSubmit = async () => {
        // Validate email
        const emailValidation = validateEmail(email);
        if (!emailValidation.isValid) {
            setEmailError(emailValidation.error);
            return;
        }

        // Check if all fields are filled
        if (!email.trim() || !subject.trim() || !message.trim()) {
            return;
        }
        
        setIsSubmitting(true);
        try {
            await onSubmit(email, subject, message);
            // Reset form on success
            setEmail("");
            setSubject("");
            setMessage("");
            setEmailError("");
            onClose();
        } catch (error) {
            console.error("Error sharing:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setEmail("");
        setSubject("");
        setMessage("");
        setEmailError("");
        onClose();
    };

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(e.target.value);
        if (emailError) {
            setEmailError("");
        }
    };

    const isFormValid = email.trim() && subject.trim() && message.trim() && !emailError;

    return (
        <>
            {show && (
                <div
                    className="modal-overlay"
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        zIndex: 999,
                    }}
                ></div>
            )}

            <div
                className="modal card fadeIn"
                style={{
                    display: show ? "block" : "none",
                    position: "fixed",
                    maxWidth: "43.22vw",
                    height: "55vh",
                    maxHeight: "70vh",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    zIndex: 1000,
                    border: "1px solid #dee2e6",
                    borderRadius: "1.875rem",
                    overflow: "hidden"
                }}
            >
                <div className="modal-header mx-4" style={{
                    borderBottomColor: '#5376F0'
                }}>
                    <h5 className="modal-title" style={{
                        color: '#26397E',
                        fontSize: '1.625rem',
                        fontWeight: 'bold'
                    }}>{title}</h5>
                </div>
                <div 
                    className="modal-body py-4"
                    style={{
                        height: "calc(50vh - 120px)",
                        overflowY: "auto",
                        paddingLeft: '2.5rem',
                        paddingRight: '2.5rem'
                    }}
                >
                    <div className="d-flex flex-row gap-3">
                        <div className="mb-3 w-50">
                            <label htmlFor="email" className="form-label" style={{
                                // color: '#8D07A4',
                                // fontWeight: 'bold',
                                fontSize: '0.875rem',
                                marginBottom: '0.5rem'
                            }}>
                                Receiver Email Address
                            </label>
                            <input
                                type="email"
                                name="email"
                                className={`form-control bg-white ${emailError ? 'is-invalid' : ''}`}
                                value={email}
                                onChange={handleEmailChange}
                                // placeholder="Enter recipient's email address"
                                
                            />
                            {emailError && (
                                <div className="invalid-feedback" style={{ display: 'block' }}>
                                    {emailError}
                                </div>
                            )}
                        </div>
                        
                        <div className="mb-3 w-50">
                            <label htmlFor="subject" className="form-label" style={{
                                // color: '#8D07A4',
                                // fontWeight: 'bold',
                                fontSize: '0.875rem',
                            }}>
                                Subject
                            </label>
                            <input
                                type="text"
                                name="subject"
                                className="form-control bg-white"
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                                // placeholder="Enter email subject"
                            />
                        </div>
                    </div>
                    
                    <div className="mb-3">
                        <label htmlFor="message" className="form-label" style={{
                            // color: '#8D07A4',
                            // fontWeight: 'bold',
                            fontSize: '0.875rem',
                        }}>
                            Message
                        </label>
                        <textarea
                            name="message"
                            className="form-control bg-white"
                            rows={6}
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            // placeholder="Enter your message"
                            
                        />
                    </div>
                </div>
                <div className="modal-footer d-flex flex-row align-items-center justify-content-center gap-2 px-4 pb-3" style={{
                    border: 'none',
                    paddingBottom: '1.5rem'
                }}>
                    <button 
                        className="btn btn-rounded btn-confirmation clr-black btn-confirmation-cancel"
                        onClick={handleClose}
                        disabled={isSubmitting}
                        style={{
                            width: '32%', 
                            transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                            const target = e.target as HTMLButtonElement;
                            target.style.backgroundColor = '#5376F0 !important';
                            target.style.transform = 'translateY(-2px)';
                        }}
                        onMouseLeave={(e) => {
                            const target = e.target as HTMLButtonElement;
                            target.style.backgroundColor = '#ABCAFF !important';
                            target.style.transform = 'translateY(0)';
                        }}
                    >
                        CANCEL
                    </button>
                    <button
                        className="btn btn-rounded-blue btn-confirmation"
                        onClick={handleSubmit}
                        disabled={!isFormValid || isSubmitting}
                        style={{
                            width: '32%', 
                            transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                            const target = e.target as HTMLButtonElement;
                            target.style.backgroundColor = '#5376F0 !important';
                            target.style.transform = 'translateY(-2px)';
                        }}
                        onMouseLeave={(e) => {
                            const target = e.target as HTMLButtonElement;
                            target.style.backgroundColor = '#ABCAFF !important';
                            target.style.transform = 'translateY(0)';
                        }}
                    >
                        {isSubmitting ? 'SUBMITTING...' : 'SUBMIT'}
                    </button>
                </div>
            </div>
        </>
    );
};

export default ShareModal; 